import {
  Badge,
  Button,
  LoadingSpinner,
  ShareDialog,
  SlideoutMenu
} from '@goodneighbor/ui';
import { AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import {
  ArrowLeft,
  Send01,
  Share07,
  UsersPlus
} from '@goodneighbor/ui/icons/untitled';
import styles from './eventSlideOut.module.scss';
import { type Event } from '@goodneighbor/api/schemas/events/event.schema';
import ExpandableText from '@/components/ExpandableText';
import EventMarker from '@/app/events/components/EventMarker';
import Link from 'next/link';
import useEventQuery from '@/hooks/api/events/useEventQuery';
import useEventRegistrationCountQuery from '@/hooks/api/events/useEventRegistrationCountQuery';
import { useMemo, useState } from 'react';
import { isErrorResponse } from '@/services/typeguards';
import { formatEventDate, formatEventTime } from '@/services/dateTime';
import RegistrationCounter from './components/RegistrationCounter';
import { CheckInQRCode } from './components/CheckInQRCode';
import RegistrationModal from '@/components/modals/RegistrationModal';
import ExternalRegistrationModal from '@/components/modals/ExternalRegistrationModal/ExternalRegistrationModal';
import { InviteAFriendModal } from '@/components/modals/InviteAFriendModal';
import { PartnerPreviewCard } from '@/components/PartnerPreviewCard';
import { activityTypeMap, causeTypeMap, skillTypeMap } from '@/services/event';
import { isDatePastOrEqual } from '@goodneighbor/utilities/date/index';

type EventSlideOutProps = {
  event?: Event | null;
  eventId?: string | null;
  showRegistrationCounter?: boolean;
} & Omit<React.ComponentProps<typeof SlideoutMenu>, 'children'>;

const isExternalEvent = (event: { source?: string } | null) =>
  event?.source === 'EXTERNAL';

const SidebarSection = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className={styles.sidebarSection}>
      <h3 className={styles.sidebarSectionTitle}>{title}</h3>
      <div className={styles.sidebarSectionContent}>{children}</div>
    </div>
  );
};

export default function EventSlideOut({
  event: originalEvent,
  eventId,
  showRegistrationCounter = true,
  ...props
}: EventSlideOutProps) {
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { isLoading, data, error } = useEventQuery({
    id: eventId || originalEvent?.id || ''
  });

  const event = useMemo(() => {
    if (data && !isErrorResponse(data) && data.data) {
      return data.data;
    }
    return null;
  }, [data]);

  const { data: registrationCountData } = useEventRegistrationCountQuery({
    enabled: showRegistrationCounter,
    id: event?.id || eventId || originalEvent?.id || ''
  });

  const registrationCount = useMemo(() => {
    if (
      registrationCountData &&
      !isErrorResponse(registrationCountData) &&
      registrationCountData.data
    ) {
      return registrationCountData.data.registrationCount;
    }
    return 0;
  }, [registrationCountData]);

  const isEventFull = useMemo(() => {
    if (isExternalEvent(event)) return false;
    const capacity = event?.overallCapacity ?? 0;
    return capacity > 0 && registrationCount >= capacity;
  }, [event, registrationCount]);

  const isAlreadyRegistered = event?.registered ?? false;
  const isEventPast =
    (event?.endsAt && isDatePastOrEqual(new Date(event.endsAt))) || false;
  const getButtonLabel = () => {
    if (isAlreadyRegistered) return 'Registered';
    if (isEventFull || isEventPast) return 'Registration closed';
    return 'Register Now';
  };

  const isButtonDisabled = isAlreadyRegistered || isEventFull || isEventPast;

  const eventShareUrl = useMemo(() => {
    const currentEventId = event?.id || eventId || originalEvent?.id;
    if (!currentEventId) return '';

    const origin =
      typeof window === 'undefined'
        ? process.env.NEXT_PUBLIC_VOLUNTEER_URL
        : window.location.origin;

    if (!origin) return '';

    const url = new URL('/events', origin);
    url.searchParams.set('eventId', currentEventId);
    return url.toString();
  }, [event?.id, eventId, originalEvent?.id]);

  return (
    <SlideoutMenu
      {...props}
      overlayClassName={styles.slideOutOverlay}
      className={styles.slideOutMenu}
      dialogClassName={styles.slideOutDialog}
      dialogProps={{
        'aria-label': 'Events'
      }}
    >
      {({ close }) => (
        <>
          {isLoading && (
            <div className={styles.loadingContainer}>
              <LoadingSpinner />
            </div>
          )}
          {error && !isLoading && (
            <div className={styles.error}>Error loading event data.</div>
          )}
          {event &&
            (isExternalEvent(event) ? (
              <ExternalRegistrationModal
                isOpen={registrationOpen}
                onOpenChange={setRegistrationOpen}
                event={event}
                organizationName={event.organization?.name || ''}
              />
            ) : (
              <RegistrationModal
                isOpen={registrationOpen}
                onOpenChange={setRegistrationOpen}
                event={event}
                organizationName={event.organization?.name || ''}
              />
            ))}
          {event && (
            <InviteAFriendModal
              isOpen={inviteModalOpen}
              onOpenChange={setInviteModalOpen}
              eventId={event.id}
            />
          )}
          {event && (
            <ShareDialog
              useReactAria
              isOpen={shareDialogOpen}
              onOpenChange={setShareDialogOpen}
              shareUrl={eventShareUrl}
              title="Share Event"
              description="Encourage your friends to join you in volunteering."
            />
          )}
          {!isLoading && !error && event && (
            <>
              <SlideoutMenu.Header className={styles.header} hideDefaultClose>
                <Button
                  className={styles.backLink}
                  iconLeading={() => (
                    <ArrowLeft className={styles.backLinkArrow} />
                  )}
                  color="link-gray"
                  onClick={close}
                >
                  Back to map
                </Button>
                <div className={styles.actions}>
                  <Button
                    className={styles.actionButton}
                    color="secondary"
                    size="md"
                    iconLeading={() => <Send01 width={20} height={20} />}
                    onClick={() => setInviteModalOpen(true)}
                    aria-label="Invite a friend"
                  />
                  <Button
                    className={styles.actionButton}
                    color="secondary"
                    size="md"
                    iconLeading={() => <Share07 width={20} height={20} />}
                    aria-label="Share event"
                    onClick={() => setShareDialogOpen(true)}
                  />
                </div>
              </SlideoutMenu.Header>
              <SlideoutMenu.Content className={styles.content}>
                {event?.promoImageUrl && (
                  <img
                    className={styles.promoImage}
                    src={event.promoImageUrl}
                    alt={`${event?.title} promotional image`}
                  />
                )}
                <div className={styles.contentContainer}>
                  <div className={styles.infoSection}>
                    <div className={styles.eventInfo}>
                      <h1 className={styles.title}>{event?.title}</h1>
                      <h2 className={styles.host}>
                        {event?.organization?.name}
                      </h2>
                    </div>
                    <div className={styles.section}>
                      <h3 className={styles.sectionHeading}>
                        Event Description
                      </h3>
                      <ExpandableText>
                        {event?.description || 'No description available.'}
                      </ExpandableText>
                    </div>
                    <div className={styles.badgesContainer}>
                      {!!event?.causeType && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>Cause type</h5>
                          <div className={styles.badges}>
                            <Badge type="pill-color" color="blue">
                              {causeTypeMap[event?.causeType] ||
                                event?.causeType}
                            </Badge>
                          </div>
                        </div>
                      )}
                      {!!event.activityTypes.length && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>
                            Activity type
                          </h5>
                          <div className={styles.badges}>
                            {event.activityTypes.map((activityType) => (
                              <Badge
                                key={activityType.id}
                                type="pill-color"
                                color="success"
                              >
                                {activityTypeMap[activityType.name]}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {!!event.skillTypes.length && (
                        <div className={styles.badgeSection}>
                          <h5 className={styles.badgesHeading}>Skill type</h5>
                          <div className={styles.badges}>
                            {event.skillTypes.map((skillType) => (
                              <Badge
                                key={skillType.id}
                                type="pill-color"
                                color="purple"
                              >
                                {skillTypeMap[skillType.name]}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {event && event.organization && (
                      <div className={styles.section}>
                        <h3 className={styles.sectionHeadingHost}>
                          Host Partner
                        </h3>
                        {isExternalEvent(event) ? (
                          event.organization.website ? (
                            <a
                              href={event.organization.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.partnerLink}
                            >
                              <PartnerPreviewCard
                                name={event.organization.name}
                                description={event.organization.description}
                                imageUrl={event.organization.logoImageUrl}
                              />
                            </a>
                          ) : (
                            <PartnerPreviewCard
                              name={event.organization.name}
                              description={event.organization.description}
                              imageUrl={event.organization.logoImageUrl}
                            />
                          )
                        ) : event.organization.slug ? (
                          <Link
                            href={`/organization/${event.organization.slug}`}
                          >
                            <PartnerPreviewCard
                              name={event.organization.name}
                              description={event.organization.description}
                              imageUrl={event.organization.logoImageUrl}
                            />
                          </Link>
                        ) : (
                          <PartnerPreviewCard
                            name={event.organization.name}
                            description={event.organization.description}
                            imageUrl={event.organization.logoImageUrl}
                          />
                        )}
                      </div>
                    )}
                    {!!event?.sponsors?.length && (
                      <div className={styles.section}>
                        <h3 className={styles.sectionHeadingSponsors}>
                          Event Partners
                        </h3>
                        <div className={styles.sectionSponsors}>
                          {event.sponsors.map((sponsor) => (
                            <Link
                              key={sponsor.id}
                              href={`/organization/${sponsor.slug}`}
                              className={styles.sponsorLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <PartnerPreviewCard
                                name={sponsor.name}
                                imageUrl={sponsor.logoImageUrl}
                              />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.sidebar}>
                    <div>
                      <Button
                        className={styles.registerButton}
                        size="lg"
                        color="primary"
                        onClick={() => setRegistrationOpen(true)}
                        isDisabled={isButtonDisabled}
                      >
                        {getButtonLabel()}
                      </Button>
                      {showRegistrationCounter &&
                        (isExternalEvent(event) ? (
                          <div className={styles.volunteersRequested}>
                            <UsersPlus
                              width={16}
                              height={16}
                              className={styles.volunteersRequestedIcon}
                            />
                            <span>
                              {`${event?.publicVolunteersCount ?? 0} volunteers requested`}
                            </span>
                          </div>
                        ) : (
                          <RegistrationCounter
                            eventId={event?.id || ''}
                            total={event?.overallCapacity || 0}
                            className={styles.progressBar}
                          />
                        ))}
                    </div>
                    {process.env.NEXT_PUBLIC_ENABLE_QR_CHECKIN === 'true' &&
                      isAlreadyRegistered &&
                      event.attendanceId &&
                      !isEventPast &&
                      !isExternalEvent(event) && (
                        <CheckInQRCode
                          attendanceId={event.attendanceId}
                          eventId={event.id}
                        />
                      )}
                    <SidebarSection title="Event Location">
                      {event?.locationName}
                      {event?.address && (
                        <>
                          <br />
                          {event.address}
                        </>
                      )}
                      {(event?.city || event?.region || event?.postalCode) && (
                        <>
                          <br />
                          {[event?.city, event?.region]
                            .filter(Boolean)
                            .join(', ')}
                          {event?.postalCode ? ` ${event.postalCode}` : ''}
                        </>
                      )}
                    </SidebarSection>
                    <SidebarSection title="Date and Time">
                      {event?.startsAt
                        ? formatEventDate(event.startsAt, event.timeZoneId)
                        : ''}
                      <br />
                      {event?.startsAt
                        ? formatEventTime(event.startsAt, event.timeZoneId)
                        : ''}
                      -{' '}
                      {event?.endsAt
                        ? formatEventTime(event.endsAt, event.timeZoneId, {
                            timeZoneName: 'short'
                          })
                        : ''}
                    </SidebarSection>
                    {event?.sourceUrl && isExternalEvent(event) && (
                      <SidebarSection title="Registration platform">
                        <a
                          className={styles.link}
                          href={event.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {new URL(event.sourceUrl).hostname.replace(
                            /^www\./,
                            ''
                          )}
                        </a>
                      </SidebarSection>
                    )}
                    {event?.website && !isExternalEvent(event) && (
                      <SidebarSection title="Website">
                        <a
                          className={styles.link}
                          href={event.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {event.website}
                        </a>
                      </SidebarSection>
                    )}
                    <SidebarSection title="Location">
                      <div className={styles.mapContainer}>
                        <Map
                          defaultCenter={{
                            lat: originalEvent?.coords?.coordinates[1] || 0,
                            lng: originalEvent?.coords?.coordinates[0] || 0
                          }}
                          defaultZoom={10}
                          mapId="EVENT_SLIDEOUT_MAP"
                          gestureHandling="none"
                        >
                          <AdvancedMarker
                            position={{
                              lat: originalEvent?.coords?.coordinates[1] || 0,
                              lng: originalEvent?.coords?.coordinates[0] || 0
                            }}
                            style={{
                              position: 'relative'
                            }}
                          >
                            <EventMarker />
                          </AdvancedMarker>
                        </Map>
                      </div>
                      <br />
                      {event?.city}, {event?.region}
                    </SidebarSection>
                  </div>
                </div>
              </SlideoutMenu.Content>
            </>
          )}
        </>
      )}
    </SlideoutMenu>
  );
}
